import { useState, useCallback } from "react";
import {
	getEvaluationState,
	createEvaluation,
	changeEvaluationMode,
	updateEvaluationProgress,
	advanceEvaluationStep,
} from "../api/evaluation-api";
import type {
	EvaluationState,
	CurrentUserContext,
} from "../types/evaluation.type";
import type { PanelMode } from "../types/panel.type";

interface UseEvaluationOptions {
	rfxId: number | undefined;
	rfxSubmissionId?: number;
}

interface UseEvaluationResult {
	accessError: unknown;
	currentStep: number;
	currentUserContext: CurrentUserContext | null;
	disclaimerEnabled: boolean;
	error: string | null;
	evaluationState: EvaluationState | null;
	hasStarted: boolean;
	isLoading: boolean;
	advanceStep: () => Promise<EvaluationState | null>;
	changeMode: (newMode: PanelMode) => Promise<EvaluationState | null>;
	fetchEvaluationState: () => Promise<void>;
	startEvaluation: (panelMode?: PanelMode) => Promise<EvaluationState | null>;
	updateProgress: (fields: { panel_mode?: PanelMode }) => Promise<void>;
}

export const useEvaluation = ({
	rfxId,
	rfxSubmissionId,
}: UseEvaluationOptions): UseEvaluationResult => {
	const [evaluationState, setEvaluationState] =
		useState<EvaluationState | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [accessError, setAccessError] = useState<unknown>(null);

	const fetchEvaluationState = useCallback(async (): Promise<void> => {
		if (!rfxId) {
			return;
		}
		setIsLoading(true);
		setError(null);
		setAccessError(null);
		try {
			const state = await getEvaluationState(rfxId, rfxSubmissionId);
			setEvaluationState(state);
		} catch (err: unknown) {
			setError("Failed to load evaluation state");
			setAccessError(err);
		} finally {
			setIsLoading(false);
		}
	}, [rfxId, rfxSubmissionId]);

	const handleCreateEvaluation = useCallback(
		async (panelMode?: PanelMode): Promise<EvaluationState | null> => {
			if (!rfxId) {
				return null;
			}
			setIsLoading(true);
			setError(null);
			try {
				const state = await createEvaluation(rfxId, panelMode);
				setEvaluationState(state);
				return state;
			} catch {
				setError("Failed to create evaluation");
				return null;
			} finally {
				setIsLoading(false);
			}
		},
		[rfxId]
	);

	const handleChangeMode = useCallback(
		async (newMode: PanelMode): Promise<EvaluationState | null> => {
			if (!evaluationState?.id) {
				return null;
			}
			setError(null);
			try {
				await changeEvaluationMode(evaluationState.id, newMode);
				// Re-fetch full state so current_user_context is preserved
				await fetchEvaluationState();
				return evaluationState;
			} catch (err: unknown) {
				const axiosErr = err as { response?: { data?: { message?: string } } };
				setError(
					axiosErr?.response?.data?.message ??
						"Failed to change evaluation mode"
				);
				return null;
			}
		},
		[fetchEvaluationState, evaluationState]
	);

	const handleAdvanceStep =
		useCallback(async (): Promise<EvaluationState | null> => {
			if (!evaluationState?.id) {
				return null;
			}
			try {
				const state = await advanceEvaluationStep(evaluationState.id);
				setEvaluationState((prev) => ({
					...state,
					currentUserContext:
						state.currentUserContext ?? prev?.currentUserContext ?? null,
				}));
				return state;
			} catch (err: unknown) {
				const axiosErr = err as { response?: { data?: { message?: string } } };
				setError(
					axiosErr?.response?.data?.message ??
						"Failed to advance evaluation step"
				);
				return null;
			}
		}, [evaluationState?.id]);

	const updateProgress = useCallback(
		async (fields: { panel_mode?: PanelMode }): Promise<void> => {
			if (!evaluationState?.id) {
				return;
			}
			try {
				const state = await updateEvaluationProgress(
					evaluationState.id,
					fields
				);
				setEvaluationState((prev) => ({
					...state,
					currentUserContext:
						state.currentUserContext ?? prev?.currentUserContext ?? null,
				}));
			} catch {
				// Silent fail — progress save is non-critical
			}
		},
		[evaluationState?.id]
	);

	return {
		accessError,
		evaluationState,
		hasStarted: evaluationState?.hasStarted ?? false,
		currentStep: evaluationState?.currentStep ?? 0,
		currentUserContext: evaluationState?.currentUserContext ?? null,
		disclaimerEnabled: evaluationState?.disclaimerEnabled ?? false,
		isLoading,
		error,
		advanceStep: handleAdvanceStep,
		fetchEvaluationState,
		startEvaluation: handleCreateEvaluation,
		changeMode: handleChangeMode,
		updateProgress,
	};
};
